import React, { useCallback, useEffect, useRef, useState } from "react";
import { CalendarRange, Play, Square, RefreshCw, Trophy, AlertTriangle, TrendingUp, TrendingDown, Percent } from "lucide-react";

import { api } from "@/lib/api";
import { speakForge } from "@/lib/companionVoice";


const LS_LAST_AUTOPLAY_WEEK = "forge_retro_last_autoplay_iso_week";


/** ISO-week identifier "YYYY-Www" — used to gate auto-play once per week. */
function _isoWeek(d = new Date()) {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  const wk = Math.ceil(((t - yearStart) / 86400000 + 1) / 7);
  return `${t.getUTCFullYear()}-W${String(wk).padStart(2, "0")}`;
}


/**
 * Weekly Retro card. Loads /api/companion/weekly-retro on mount.
 * Auto-plays exactly once per ISO week on Sundays (localStorage-gated).
 * Hides itself entirely when the endpoint fails.
 */
export function WeeklyRetroCard() {
  const [retro, setRetro] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const speakStopRef = useRef(null);
  const autoplayedRef = useRef(false);

  const load = useCallback(async (force = false) => {
    try {
      const { data } = await api.get("/companion/weekly-retro",
        { params: force ? { force: 1 } : {} });
      setRetro(data);
      return data;
    } catch (err) {
      console.debug("[retro] load failed", err);
      return null;
    } finally {
      setLoading(false);
      setRegenerating(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const stop = useCallback(() => {
    try { speakStopRef.current?.(); } catch { /* noop */ }
    speakStopRef.current = null;
    setPlaying(false);
  }, []);

  const play = useCallback(async (text) => {
    if (!text) return;
    stop();
    setPlaying(true);
    try {
      const handle = await speakForge(text, {
        voice: localStorage.getItem("forge_voice") || "nova",
        onEnd: () => setPlaying(false),
      });
      speakStopRef.current = handle.stop;
    } catch (err) {
      setPlaying(false);
      console.debug("[retro] speak failed", err);
    }
  }, [stop]);

  // Sunday-only weekly auto-play, once per ISO week, gated on successful audio start
  useEffect(() => {
    if (autoplayedRef.current || !retro?.text) return;
    // Only auto-play on Sundays (dayOfWeek === 0)
    if (new Date().getDay() !== 0) return;
    const wk = _isoWeek();
    const last = localStorage.getItem(LS_LAST_AUTOPLAY_WEEK);
    if (last === wk) return;
    autoplayedRef.current = true;
    const t = setTimeout(async () => {
      try {
        await play(retro.text);
        localStorage.setItem(LS_LAST_AUTOPLAY_WEEK, wk);
      } catch {
        // autoplay blocked — don't stamp, retry on next visit
      }
    }, 1200);
    return () => clearTimeout(t);
  }, [retro, play]);

  useEffect(() => () => stop(), [stop]);

  if (loading || !retro) return null;

  const stats = retro.stats || {};
  const pnl = stats.total_pnl_7d ?? 0;
  const pnlClass = pnl > 0 ? "text-[var(--profit)]" : pnl < 0 ? "text-[var(--loss)]" : "text-[var(--text-secondary)]";

  return (
    <section className="card-sharp p-5 mb-6" data-testid="weekly-retro-card">
      <div className="flex items-center gap-2 mb-3">
        <CalendarRange size={14} className="text-[var(--accent)]"/>
        <h3 className="font-display text-lg text-white flex-1">Weekly retro</h3>
        {retro.cached && <span className="chip">cached</span>}
        <button data-testid="retro-regenerate"
          onClick={async () => { setRegenerating(true); await load(true); }}
          disabled={regenerating}
          title="Regenerate with fresh data"
          className="p-1.5 border border-white/10 rounded hover:border-[var(--accent)] disabled:opacity-50">
          <RefreshCw size={12} className={regenerating ? "animate-spin" : ""}/>
        </button>
        {playing ? (
          <button data-testid="retro-stop" onClick={stop}
            className="btn-primary text-xs px-3 py-1.5 flex items-center gap-1.5">
            <Square size={11}/> Stop
          </button>
        ) : (
          <button data-testid="retro-play" onClick={() => play(retro.text)}
            className="btn-primary text-xs px-3 py-1.5 flex items-center gap-1.5">
            <Play size={11}/> Play
          </button>
        )}
      </div>

      <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-4"
        data-testid="retro-text">
        {retro.text}
      </p>

      <div className="flex flex-wrap gap-2">
        <StatChip icon={pnl >= 0 ? <TrendingUp size={11}/> : <TrendingDown size={11}/>}
          label={`${pnl >= 0 ? "+" : ""}${pnl.toFixed(2)} USD`}
          sub={`7-day PnL · ${stats.trades_count || 0} closed`}
          className={pnlClass}
          testId="retro-stat-pnl"/>
        <StatChip icon={<Percent size={11}/>}
          label={`${(stats.win_rate_pct || 0).toFixed(1)}% win`}
          sub={`${stats.winners_7d || 0}W · ${stats.losers_7d || 0}L`}
          testId="retro-stat-winrate"/>
        {stats.best_strategy?.name && (
          <StatChip icon={<Trophy size={11}/>}
            label={stats.best_strategy.name}
            sub={`best · ${stats.best_strategy.pnl >= 0 ? "+" : ""}${stats.best_strategy.pnl.toFixed(2)}`}
            className="text-[var(--profit)]"
            testId="retro-stat-best"/>
        )}
        {stats.top_mistake && (
          <StatChip icon={<AlertTriangle size={11}/>}
            label={stats.top_mistake}
            sub={`${stats.top_mistake_count}× this week`}
            className="text-[var(--warning)]"
            testId="retro-stat-mistake"/>
        )}
      </div>
    </section>
  );
}


function StatChip({ icon, label, sub, className = "text-white", testId }) {
  return (
    <div className="border border-white/10 rounded px-2.5 py-1.5 bg-black/30 flex items-center gap-1.5"
      data-testid={testId}>
      {icon && <span className={className}>{icon}</span>}
      <div>
        <div className={`text-[11px] font-mono ${className}`}>{label}</div>
        {sub && <div className="text-[8.5px] font-mono text-[var(--text-muted)] uppercase tracking-wide">{sub}</div>}
      </div>
    </div>
  );
}
